import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Icon,
  IconButton,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core"
import { Alert, Autocomplete } from "@material-ui/lab"
import { PhoneInput, UnsavedChangesConfirm } from "components"
import { UnsavedChangesConfirmRef } from "components/UnsavedChangesConfirm/UnsavedChangesConfirm"
import { ManagementContext } from "context"
import {
  createCustomerService,
  EDIT_PATH,
  editCustomerService,
  FIELDS,
} from "lib"
import React from "react"
import { useHistory, useLocation } from "react-router"
import { IContact, ICustomer, IMessageConfig } from "types"
import isEmail from "validator/lib/isEmail"

interface Props {
  onSuccess: () => void
  onError: (message?: string) => void
  onClose: () => void
}

const emptyContact = (): IContact =>
  ({ type: "Retenciones", active: true }) as IContact

const CustomerForm = (props: Props) => {
  const location = useLocation()
  const history = useHistory()
  const { onError, onSuccess, onClose } = props
  const { identificationTypes } = React.useContext(ManagementContext)
  const initialInput = React.useRef<any>(null)
  const unsavedRef = React.useRef<UnsavedChangesConfirmRef>(null)

  const [loading, setLoading] = React.useState(false)
  const [submitted, setSubmitted] = React.useState(false)
  const [isDirty, setIsDirty] = React.useState(false)

  // Dialog de contacto
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false)
  const [contactSubmitted, setContactSubmitted] = React.useState(false)
  const [editIndex, setEditIndex] = React.useState<number>(-1)
  const [contact, setContact] = React.useState<IContact>(emptyContact())

  const [customer, setCustomer] = React.useState<ICustomer>({
    active: true,
    contacts: [] as IContact[],
    customerCode: "",
  } as ICustomer)

  const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({
    open: false,
    message: "",
    severity: "info",
  })
  const [pendingContactWarning, setPendingContactWarning] =
    React.useState(false)
  const [emailError, setEmailError] = React.useState<string>("")

  const contactList: IContact[] = customer.contacts ?? []

  // ── Guards ──────────────────────────────────────────────
  const handleCloseWithGuard = () => {
    if (isDirty) {
      unsavedRef.current?.open()
      return
    }
    onClose()
  }

  // ── Submit cliente ───────────────────────────────────────
  const handleSumbit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault()
    setSubmitted(true)
    if (!customer.customerCode) return
    try {
      setLoading(true)
      const finalContacts = contactList.filter(
        (c) => c.email || c.name || c.phone,
      )
      const payload = {
        identification: customer.identification,
        identificationType: customer.identificationType.identificationTypeId,
        companyName: customer.companyName,
        commercialName: customer.commercialName,
        customerCode: customer.customerCode,
        specialTaxpayer: customer.specialTaxpayer,
        keepAccounts: customer.keepAccounts,
        active: customer.active,
        categoryId: customer.category?.categoryId,
        contacts: finalContacts,
      }
      if (!(location.state as any)?.edit) {
        await createCustomerService(payload)
      } else {
        await editCustomerService(customer.customerId, payload)
      }
      setLoading(false)
      if (!(location.state as any)?.edit) {
        setCustomer({
          active: true,
          contacts: [] as IContact[],
          customerCode: "",
        } as ICustomer)
        initialInput.current?.focus()
      }
      setSubmitted(false)
      setIsDirty(false)
      onSuccess()
    } catch (error: any) {
      setLoading(false)
      onError(error.message ?? undefined)
    }
  }

  React.useEffect(() => {
    if ((location.state as any)?.edit) {
      if ((location.state as any)?.values) {
        setCustomer((location.state as any)?.values)
      }
    } else {
      history.replace(location.pathname.replace(EDIT_PATH, ""))
    }
  }, [location.state, history, location.pathname])

  // ── Dialog de contacto ───────────────────────────────────
  const openNewContact = () => {
    setContact(emptyContact())
    setContactSubmitted(false)
    setEmailError("")
    setEditIndex(-1)
    setContactDialogOpen(true)
  }

  const openEditContact = (c: IContact, index: number) => {
    setContact(c)
    setContactSubmitted(false)
    setEmailError("")
    setEditIndex(index)
    setContactDialogOpen(true)
  }

  const closeContactDialog = () => {
    setContactDialogOpen(false)
    setContactSubmitted(false)
    setEmailError("")
    setContact(emptyContact())
    setEditIndex(-1)
  }

  const handleContactSave = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault()
    setContactSubmitted(true)

    if (
      !contact.name ||
      !contact.email ||
      !contact.phone ||
      !contact.description
    )
      return

    const emailAlreadyAdded =
      customer.contacts !== null &&
      customer.contacts.some(
        (c, i) => c.email === contact.email && i !== editIndex,
      )

    if (emailAlreadyAdded) {
      setEmailError("Este correo ya está registrado en otro contacto")
      return
    }

    if (!isEmail(contact.email)) {
      setEmailError("Ingresa un email válido")
      return
    }

    setCustomer((cur) => ({
      ...cur,
      contacts:
        cur.contacts !== null
          ? editIndex !== -1
            ? cur.contacts.map((c, i) => (i === editIndex ? contact : c))
            : [...cur.contacts, contact]
          : [contact],
    }))
    setIsDirty(true)
    closeContactDialog()
  }

  const handleDeleteContact = (idx: number) => {
    setCustomer((cur) => ({
      ...cur,
      contacts: cur.contacts.filter((_, i) => i !== idx),
    }))
    setIsDirty(true)
  }

  const isEditing = editIndex !== -1

  return (
    <div className="flex flex-col">
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderBottom: "1px solid #e0e0e0" }}
      >
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 700, letterSpacing: 0.4 }}
        >
          {(location.state as any)?.edit ? "Editar cliente" : "Nuevo cliente"}
        </Typography>
        <IconButton onClick={handleCloseWithGuard} size="small">
          <Icon fontSize="small">close</Icon>
        </IconButton>
      </div>

      {/* ── Body ── */}
      <div className="flex">
        {/* Columna izquierda: datos del cliente */}
        <div
          className="flex flex-col px-5 py-4"
          style={{
            width: "50%",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          <div className="flex items-center mb-4" style={{ gap: 8 }}>
            <Icon color="action" fontSize="small">
              business
            </Icon>
            <Typography
              variant="caption"
              style={{
                fontWeight: 700,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: "#666",
              }}
            >
              Datos del cliente
            </Typography>
          </div>

          <div className="flex flex-col" style={{ gap: 12 }}>
            <TextField
              inputRef={initialInput}
              autoFocus
              size="small"
              label={FIELDS.identification.name}
              variant="outlined"
              fullWidth
              required
              value={customer.identification ?? ""}
              onChange={(e) => {
                setIsDirty(true)
                setCustomer((cur) => ({
                  ...cur,
                  identification: e.target.value,
                }))
              }}
            />
            <Autocomplete
              options={identificationTypes}
              getOptionLabel={(t) => t.name}
              fullWidth
              value={customer.identificationType ?? null}
              openOnFocus
              size="small"
              onChange={(_, val) => {
                if (val) {
                  setIsDirty(true)
                  setCustomer((cur) => ({ ...cur, identificationType: val }))
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  fullWidth
                  label="Tipo de identificación"
                  required
                />
              )}
            />
            <TextField
              size="small"
              label={FIELDS.companyName.name}
              variant="outlined"
              fullWidth
              required
              value={customer.companyName ?? ""}
              onChange={(e) => {
                setIsDirty(true)
                setCustomer((cur) => ({ ...cur, companyName: e.target.value }))
              }}
            />
            <TextField
              size="small"
              label="Cuenta del cliente"
              variant="outlined"
              fullWidth
              required
              value={customer.customerCode ?? ""}
              onChange={(e) => {
                setIsDirty(true)
                setCustomer((cur) => ({ ...cur, customerCode: e.target.value }))
              }}
              error={submitted && !customer.customerCode}
              helperText={
                submitted && !customer.customerCode ? "Campo obligatorio" : ""
              }
            />
            <TextField
              size="small"
              label={FIELDS.comercialName.name}
              variant="outlined"
              fullWidth
              required
              value={customer.commercialName ?? ""}
              onChange={(e) => {
                setIsDirty(true)
                setCustomer((cur) => ({
                  ...cur,
                  commercialName: e.target.value,
                }))
              }}
            />
            <FormGroup row style={{ paddingLeft: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={Boolean(customer.active)}
                    onChange={(e) =>
                      setCustomer((cur) => ({
                        ...cur,
                        active: e.target.checked,
                      }))
                    }
                    name={FIELDS.active.name}
                  />
                }
                label={
                  <Typography variant="body2">
                    {FIELDS.activeAlt.name}
                  </Typography>
                }
              />
            </FormGroup>
          </div>
        </div>

        {/* Columna derecha: contactos */}
        <div className="flex flex-col flex-grow px-5 py-4">
          {/* Header de la sección */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center" style={{ gap: 8 }}>
              <Icon color="action" fontSize="small">
                perm_contact_calendar
              </Icon>
              <Typography
                variant="caption"
                style={{
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  color: "#666",
                }}
              >
                Contactos del cliente
              </Typography>
              {contactList.length > 0 && (
                <Chip
                  size="small"
                  label={contactList.length}
                  color="primary"
                  style={{ height: 18, fontSize: "0.65rem", minWidth: 24 }}
                />
              )}
            </div>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disableElevation
              onClick={openNewContact}
            >
              <Icon fontSize="small" style={{ marginRight: 5 }}>
                add
              </Icon>
              Nuevo contacto
            </Button>
          </div>

          <Divider />

          {/* Lista con scroll fijo */}
          {contactList.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center flex-grow"
              style={{ gap: 8, paddingTop: 32 }}
            >
              <Icon style={{ fontSize: 48, color: "#ccc" }}>
                people_outline
              </Icon>
              <Typography variant="body2" style={{ color: "#aaa" }}>
                Sin contactos aún
              </Typography>
              <Typography
                variant="caption"
                style={{ color: "#bbb", textAlign: "center" }}
              >
                Presiona "Nuevo contacto" para agregar uno.
              </Typography>
            </div>
          ) : (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxHeight: 270,
                overflowY: "scroll",
                paddingRight: 4,
              }}
            >
              {contactList.map((c, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    padding: "10px 14px",
                    flexShrink: 0,
                    backgroundColor: "#fff",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <Avatar
                      style={{
                        width: 36,
                        height: 36,
                        fontSize: "1rem",
                        backgroundColor: "#1976d2",
                        flexShrink: 0,
                      }}
                    >
                      {c.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {c.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={c.active ? "Activo" : "Inactivo"}
                          style={{
                            height: 16,
                            fontSize: "0.62rem",
                            flexShrink: 0,
                            backgroundColor: c.active ? "#e8f5e9" : "#f5f5f5",
                            color: c.active ? "#388e3c" : "#999",
                          }}
                        />
                      </div>
                      <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                        {c.email}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {c.phone}{c.description ? ` · ${c.description}` : ""}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEditContact(c, index)}>
                        <Icon fontSize="small">edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => handleDeleteContact(index)}>
                        <Icon fontSize="small">delete_outline</Icon>
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div
        className="flex items-center justify-end px-5 py-3"
        style={{ borderTop: "1px solid #e0e0e0", gap: 8 }}
      >
        <Button size="small" onClick={handleCloseWithGuard}>
          Cancelar
        </Button>
        <Button
          size="small"
          color="primary"
          variant="contained"
          disabled={loading}
          onClick={handleSumbit}
          disableElevation
        >
          {loading ? (
            <CircularProgress
              style={{ color: "white" }}
              size={16}
              className="my-1"
              thickness={10}
            />
          ) : (location.state as any)?.edit ? (
            "Guardar cambios"
          ) : (
            "Registrar cliente"
          )}
        </Button>
      </div>

      {/* ── Dialog de contacto ── */}
      <Dialog
        open={contactDialogOpen}
        onClose={closeContactDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ paddingBottom: 8 }}>
          <div className="flex items-center" style={{ gap: 8 }}>
            <Icon color="action" fontSize="small">
              {isEditing ? "edit" : "person_add"}
            </Icon>
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              {isEditing ? "Editar contacto" : "Nuevo contacto"}
            </Typography>
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="flex flex-col" style={{ gap: 14, paddingTop: 4 }}>
            <TextField
              label={FIELDS.name.name}
              size="small"
              value={contact.name ?? ""}
              required
              fullWidth
              autoFocus
              variant="outlined"
              onChange={(e) =>
                setContact((cur) => ({ ...cur, name: e.target.value }))
              }
              error={contactSubmitted && !contact.name}
              helperText={
                contactSubmitted && !contact.name ? "Campo obligatorio" : ""
              }
            />

            <div className="flex" style={{ gap: 12 }}>
              <TextField
                label={FIELDS.email.name}
                size="small"
                required
                fullWidth
                variant="outlined"
                value={contact.email ?? ""}
                onChange={(e) => {
                  setEmailError("")
                  setContact((cur) => ({ ...cur, email: e.target.value }))
                }}
                error={!!(contactSubmitted && !contact.email) || !!emailError}
                helperText={
                  emailError
                    ? emailError
                    : contactSubmitted && !contact.email
                      ? "Campo obligatorio"
                      : ""
                }
              />
              <TextField
                label={FIELDS.phoneNumber.name}
                size="small"
                required
                fullWidth
                variant="outlined"
                value={contact.phone ?? ""}
                onChange={(e) =>
                  setContact((cur) => ({ ...cur, phone: e.target.value }))
                }
                error={contactSubmitted && !contact.phone}
                helperText={
                  contactSubmitted && !contact.phone ? "Campo obligatorio" : ""
                }
                InputProps={{ inputComponent: PhoneInput as any }}
              />
            </div>

            <TextField
              label={FIELDS.description.name}
              size="small"
              fullWidth
              required
              variant="outlined"
              value={contact.description ?? ""}
              onChange={(e) =>
                setContact((cur) => ({ ...cur, description: e.target.value }))
              }
              error={contactSubmitted && !contact.description}
              helperText={
                contactSubmitted && !contact.description
                  ? "Campo obligatorio"
                  : ""
              }
            />

            <FormGroup row style={{ paddingLeft: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    size="small"
                    checked={Boolean(contact.active)}
                    onChange={(e) =>
                      setContact((cur) => ({
                        ...cur,
                        active: e.target.checked,
                      }))
                    }
                  />
                }
                label={<Typography variant="body2">Activo</Typography>}
              />
            </FormGroup>
          </div>
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px" }}>
          <Button size="small" onClick={closeContactDialog}>
            Cancelar
          </Button>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            onClick={handleContactSave}
          >
            <Icon fontSize="small" style={{ marginRight: 5 }}>
              {isEditing ? "save" : "add"}
            </Icon>
            {isEditing ? "Guardar cambios" : "Agregar contacto"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbars ── */}
      <Snackbar
        open={messageConfig.open}
        autoHideDuration={6000}
        onClose={() => setMessageConfig({ ...messageConfig, open: false })}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Alert
          variant="filled"
          onClose={() => setMessageConfig({ ...messageConfig, open: false })}
          severity={messageConfig.severity}
        >
          {messageConfig.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={pendingContactWarning}
        autoHideDuration={5000}
        onClose={() => setPendingContactWarning(false)}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <Alert
          variant="filled"
          severity="warning"
          onClose={() => setPendingContactWarning(false)}
        >
          Tienes un contacto sin agregar. Complétalo o ciérralo antes de
          continuar.
        </Alert>
      </Snackbar>

      <UnsavedChangesConfirm
        ref={unsavedRef}
        onSave={() => {
          const syntheticEvent = {
            preventDefault: () => {},
          } as React.MouseEvent<HTMLButtonElement, MouseEvent>
          handleSumbit(syntheticEvent)
        }}
        onDiscard={() => {
          setIsDirty(false)
          onClose()
        }}
      />
    </div>
  )
}

export default CustomerForm
